/**
 * Loan v3.5 — Phase B-3: Pre-Profile Assessment Wizard (admin POC).
 *
 * Renders inside a Dialog from the AdminLoanCMS Loan Cases tab. Multi-step:
 *   1. Personal / Employment
 *   2. Income (conditional sub-form based on Income Type)
 *   3. Property & Loan
 *   4. CTOS upload + manual keying of 6 critical fields
 *   5. PDPA consent
 *   6. Submit → backend runs assessment → result view (delegated to AssessmentResult.jsx)
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { ChevronLeft, ChevronRight, Upload, Loader2, FileText, ShieldCheck } from 'lucide-react';
import AssessmentResultPanel from './AssessmentResultPanel';

const STATES = ['Johor', 'KL', 'Selangor', 'Penang', 'Perak', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perlis', 'Sabah', 'Sarawak', 'Terengganu', 'Labuan', 'Putrajaya'];

const DEFAULTS = {
  applicant: {
    nric: '', full_name: '', date_of_birth: '', nationality: 'MY',
    marital_status: 'single', education_level: 'Degree', dependents: 0,
    income_type: 'salaried_my', employer_name: '', months_in_current_job: 12,
    on_probation: false, newly_changed_job: false, no_employment_verification: false,
    basic_salary: 0, ot_allowance_bonus: 0,
    commission_avg: 0, base_retainer: 0,
    director_fee: 0, ssm_business_income: 0, ssm_company_age: '2y+',
    rental_other_income: 0,
    sg_basic_salary_sgd: 0, sg_ot_sgd: 0,
    joint_application: false, spouse_nric: '', spouse_basic_salary: 0, spouse_other_income: 0,
  },
  property_loan: {
    spa_price: 0, property_type: 'apartment', tenure_type: 'freehold',
    tenure_years: 30, state: 'Selangor', project_name: '',
    new_development: true, subsales: false, bumi_unit: false,
    existing_housing_loans_count: 0, target_loan_margin_percent: 90,
    interest_rate_percent: 4.35, loan_type: 'term_loan',
    down_payment_source: 'cash',
  },
  ctos: {
    ctos_score: 700, total_ccris_monthly_commitments: 0,
    active_facilities_count: 0, late_payments_12mo_count: 0,
    special_attention_flags_count: 0,
    has_court_action: false, has_bankruptcy: false, has_dcheqs: false,
    raw_notes: '',
  },
  pdpa_consent: false,
};

const STEPS = [
  { key: 'personal', label: 'Personal' },
  { key: 'income', label: 'Income' },
  { key: 'property', label: 'Property & Loan' },
  { key: 'ctos', label: 'CTOS + CCRIS' },
  { key: 'consent', label: 'PDPA Consent' },
];

export default function AssessmentWizard({ caseId, open, onClose, onCompleted }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(DEFAULTS);
  const [pdfFile, setPdfFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // CTOS AI auto-extract state
  const [extracting, setExtracting] = useState(false);
  const [extractedAt, setExtractedAt] = useState(null);
  const [extractNote, setExtractNote] = useState(null);

  const onPdfChange = async (file) => {
    setPdfFile(file);
    setExtractedAt(null);
    setExtractNote(null);
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file'); return;
    }
    // Fire & forget — extraction runs in background while admin continues filling
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('ctos_pdf', file);
      const r = await api.post('/loans/assessments/ctos/extract', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      if (r.data && r.data.ok && r.data.data) {
        const d = r.data.data;
        setForm(prev => ({
          ...prev,
          ctos: {
            ...prev.ctos,
            ctos_score: d.ctos_score ?? prev.ctos.ctos_score,
            total_ccris_monthly_commitments: d.total_ccris_monthly_commitments ?? prev.ctos.total_ccris_monthly_commitments,
            active_facilities_count: d.active_facilities_count ?? prev.ctos.active_facilities_count,
            late_payments_12mo_count: d.late_payments_12mo_count ?? prev.ctos.late_payments_12mo_count,
            special_attention_flags_count: d.special_attention_flags_count ?? prev.ctos.special_attention_flags_count,
            has_court_action: d.has_court_action ?? prev.ctos.has_court_action,
            has_bankruptcy: d.has_bankruptcy ?? prev.ctos.has_bankruptcy,
            has_dcheqs: d.has_dcheqs ?? prev.ctos.has_dcheqs,
            raw_notes: d.extraction_notes ? `AI: ${d.extraction_notes}` : prev.ctos.raw_notes,
          },
        }));
        setExtractedAt(new Date());
        setExtractNote(d.confidence ? `Confidence: ${d.confidence}` : null);
        toast.success('CTOS auto-extracted — please verify the 6 fields below', { duration: 4500 });
      } else {
        setExtractNote(r.data?.error || 'Auto-extract not available — please fill manually');
        toast.info('Auto-extract unavailable, fill the 6 fields manually');
      }
    } catch (e) {
      setExtractNote(e.response?.data?.detail || 'Extraction error — fill manually');
      toast.info('Auto-extract failed, please fill the 6 fields manually');
    } finally {
      setExtracting(false);
    }
  };

  const set = (section, key, value) =>
    setForm(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.applicant.nric || !form.applicant.full_name) {
        toast.error('NRIC and Full Name required'); return false;
      }
    }
    if (step === 1) {
      const a = form.applicant;
      if (a.income_type === 'salaried_my' && !a.basic_salary) { toast.error('Basic salary required'); return false; }
      if (a.income_type === 'salaried_sg' && !a.sg_basic_salary_sgd) { toast.error('SGD basic required'); return false; }
      if (a.income_type === 'business_owner' && !a.ssm_business_income) { toast.error('SSM income required'); return false; }
    }
    if (step === 2) {
      if (!form.property_loan.spa_price) { toast.error('SPA price required'); return false; }
    }
    if (step === 3) {
      if (!pdfFile) { toast.error('CTOS PDF upload is mandatory'); return false; }
      if (!form.ctos.ctos_score || form.ctos.ctos_score < 300 || form.ctos.ctos_score > 850) {
        toast.error('CTOS Score must be 300-850'); return false;
      }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep(s => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep(s => Math.max(s - 1, 0));

  const submit = async () => {
    if (!form.pdpa_consent) { toast.error('PDPA consent is required'); return; }
    if (!pdfFile) { toast.error('CTOS PDF missing'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(form));
      fd.append('ctos_pdf', pdfFile);
      const r = await api.post(`/loans/cases/${caseId}/assessment`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Assessment complete');
      setResult(r.data);
      onCompleted && onCompleted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Assessment failed');
    } finally { setBusy(false); }
  };

  const a = form.applicant;
  const p = form.property_loan;
  const c = form.ctos;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setStep(0); setResult(null); setPdfFile(null); setForm(DEFAULTS); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="assessment-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600" />
            {result ? 'Assessment Result' : `Pre-Profile Assessment — Step ${step + 1} of ${STEPS.length}`}
          </DialogTitle>
          <DialogDescription>
            {result
              ? 'Run completed. Review below — re-run any time by closing and re-opening the wizard.'
              : 'Collect customer information + upload CTOS report → run per-bank assessment.'}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <AssessmentResultPanel result={result} />
        ) : (
          <>
            {/* Step indicator */}
            <div className="flex items-center gap-1 text-xs">
              {STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                  <span
                    className={`px-2 py-1 rounded ${i === step ? 'bg-indigo-600 text-white' : i < step ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                    data-testid={`wizard-step-${s.key}`}
                  >
                    {i + 1}. {s.label}
                  </span>
                  {i < STEPS.length - 1 && <span className="text-gray-300">›</span>}
                </React.Fragment>
              ))}
            </div>

            {/* STEP 1 — Personal */}
            {step === 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>NRIC / Passport *</Label><Input value={a.nric} onChange={e => set('applicant', 'nric', e.target.value)} data-testid="aw-nric" /></div>
                <div><Label>Full Name *</Label><Input value={a.full_name} onChange={e => set('applicant', 'full_name', e.target.value)} data-testid="aw-name" /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={a.date_of_birth} onChange={e => set('applicant', 'date_of_birth', e.target.value)} data-testid="aw-dob" /></div>
                <div><Label>Nationality</Label>
                  <Select value={a.nationality} onValueChange={v => set('applicant', 'nationality', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MY">Malaysian</SelectItem>
                      <SelectItem value="PR">PR Malaysia</SelectItem>
                      <SelectItem value="SG">Singaporean</SelectItem>
                      <SelectItem value="FOREIGN">Other Foreign</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Marital Status</Label>
                  <Select value={a.marital_status} onValueChange={v => set('applicant', 'marital_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Education Level</Label>
                  <Select value={a.education_level} onValueChange={v => set('applicant', 'education_level', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Secondary">Secondary</SelectItem>
                      <SelectItem value="Diploma">Diploma</SelectItem>
                      <SelectItem value="Degree">Degree</SelectItem>
                      <SelectItem value="Master">Master & Above</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Dependents</Label><Input type="number" min={0} value={a.dependents} onChange={e => set('applicant', 'dependents', parseInt(e.target.value || 0))} /></div>
                <div><Label>Months in current job</Label><Input type="number" min={0} value={a.months_in_current_job} onChange={e => set('applicant', 'months_in_current_job', parseInt(e.target.value || 0))} /></div>
                <div className="col-span-1 sm:col-span-2 grid grid-cols-3 gap-2 mt-2">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={a.on_probation} onCheckedChange={v => set('applicant', 'on_probation', !!v)} data-testid="aw-probation" /> On probation</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={a.newly_changed_job} onCheckedChange={v => set('applicant', 'newly_changed_job', !!v)} /> Newly changed job</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={a.no_employment_verification} onCheckedChange={v => set('applicant', 'no_employment_verification', !!v)} /> No emp verification</label>
                </div>
              </div>
            )}

            {/* STEP 2 — Income */}
            {step === 1 && (
              <div className="space-y-3">
                <div><Label>Income Type</Label>
                  <Select value={a.income_type} onValueChange={v => set('applicant', 'income_type', v)}>
                    <SelectTrigger data-testid="aw-income-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="salaried_my">Salaried (MY)</SelectItem>
                      <SelectItem value="salaried_sg">Salaried (SG)</SelectItem>
                      <SelectItem value="commission">Commission Earner</SelectItem>
                      <SelectItem value="business_owner">Business Owner (SSM)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {a.income_type === 'salaried_my' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>Basic Salary (RM/mo) *</Label><Input type="number" value={a.basic_salary} onChange={e => set('applicant', 'basic_salary', parseFloat(e.target.value || 0))} data-testid="aw-basic-salary" /></div>
                    <div><Label>OT / Allowance / Bonus (RM/mo)</Label><Input type="number" value={a.ot_allowance_bonus} onChange={e => set('applicant', 'ot_allowance_bonus', parseFloat(e.target.value || 0))} /></div>
                  </div>
                )}
                {a.income_type === 'salaried_sg' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>Basic Salary (SGD/mo) *</Label><Input type="number" value={a.sg_basic_salary_sgd} onChange={e => set('applicant', 'sg_basic_salary_sgd', parseFloat(e.target.value || 0))} /></div>
                    <div><Label>OT / Allowance (SGD/mo)</Label><Input type="number" value={a.sg_ot_sgd} onChange={e => set('applicant', 'sg_ot_sgd', parseFloat(e.target.value || 0))} /></div>
                    <p className="text-xs text-gray-500 col-span-2">Converted to MYR @ 3.45 (configurable in engine).</p>
                  </div>
                )}
                {a.income_type === 'commission' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>Base / Retainer (RM/mo)</Label><Input type="number" value={a.base_retainer} onChange={e => set('applicant', 'base_retainer', parseFloat(e.target.value || 0))} /></div>
                    <div><Label>Avg Commission (RM/mo) *</Label><Input type="number" value={a.commission_avg} onChange={e => set('applicant', 'commission_avg', parseFloat(e.target.value || 0))} /></div>
                  </div>
                )}
                {a.income_type === 'business_owner' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label>SSM Business Income (RM/mo) *</Label><Input type="number" value={a.ssm_business_income} onChange={e => set('applicant', 'ssm_business_income', parseFloat(e.target.value || 0))} /></div>
                    <div><Label>Director Fee (RM/mo)</Label><Input type="number" value={a.director_fee} onChange={e => set('applicant', 'director_fee', parseFloat(e.target.value || 0))} /></div>
                    <div className="col-span-2"><Label>SSM Company Age</Label>
                      <Select value={a.ssm_company_age} onValueChange={v => set('applicant', 'ssm_company_age', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="<2y">Less than 2 years</SelectItem>
                          <SelectItem value="2y+">2 years or more</SelectItem>
                          <SelectItem value="3y+">3 years or more</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t">
                  <div><Label>Rental / Other Income (RM/mo)</Label><Input type="number" value={a.rental_other_income} onChange={e => set('applicant', 'rental_other_income', parseFloat(e.target.value || 0))} /></div>
                </div>
                <div className="space-y-2 pt-3 border-t">
                  <label className="flex items-center gap-2"><Checkbox checked={a.joint_application} onCheckedChange={v => set('applicant', 'joint_application', !!v)} data-testid="aw-joint" /> <span className="text-sm">Joint application with spouse</span></label>
                  {a.joint_application && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pl-6">
                      <div><Label>Spouse NRIC</Label><Input value={a.spouse_nric} onChange={e => set('applicant', 'spouse_nric', e.target.value)} /></div>
                      <div><Label>Spouse Basic (RM/mo)</Label><Input type="number" value={a.spouse_basic_salary} onChange={e => set('applicant', 'spouse_basic_salary', parseFloat(e.target.value || 0))} /></div>
                      <div><Label>Spouse Other (RM/mo)</Label><Input type="number" value={a.spouse_other_income} onChange={e => set('applicant', 'spouse_other_income', parseFloat(e.target.value || 0))} /></div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* STEP 3 — Property */}
            {step === 2 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>SPA Price (RM) *</Label><Input type="number" value={p.spa_price} onChange={e => set('property_loan', 'spa_price', parseFloat(e.target.value || 0))} data-testid="aw-spa-price" /></div>
                <div><Label>Target Loan Margin (%)</Label>
                  <Select value={String(p.target_loan_margin_percent)} onValueChange={v => set('property_loan', 'target_loan_margin_percent', parseInt(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{[70, 80, 85, 90, 95].map(x => <SelectItem key={x} value={String(x)}>{x}%</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Property Type</Label>
                  <Select value={p.property_type} onValueChange={v => set('property_loan', 'property_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="landed">Landed / Terrace</SelectItem>
                      <SelectItem value="apartment">Apartment / Condo</SelectItem>
                      <SelectItem value="commercial">Commercial / Shop</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Tenure Type</Label>
                  <Select value={p.tenure_type} onValueChange={v => set('property_loan', 'tenure_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="freehold">Freehold</SelectItem><SelectItem value="leasehold">Leasehold</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Tenure (years)</Label><Input type="number" min={5} max={35} value={p.tenure_years} onChange={e => set('property_loan', 'tenure_years', parseInt(e.target.value || 30))} /></div>
                <div><Label>State</Label>
                  <Select value={p.state} onValueChange={v => set('property_loan', 'state', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Project Name</Label><Input value={p.project_name} onChange={e => set('property_loan', 'project_name', e.target.value)} /></div>
                <div><Label>Interest Rate (% p.a.)</Label><Input type="number" step="0.01" value={p.interest_rate_percent} onChange={e => set('property_loan', 'interest_rate_percent', parseFloat(e.target.value || 0))} /></div>
                <div><Label>Loan Type</Label>
                  <Select value={p.loan_type} onValueChange={v => set('property_loan', 'loan_type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="term_loan">Term Loan</SelectItem><SelectItem value="flexi">Flexi</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Existing housing loans</Label>
                  <Select value={String(p.existing_housing_loans_count)} onValueChange={v => set('property_loan', 'existing_housing_loans_count', parseInt(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{[0, 1, 2, 3].map(x => <SelectItem key={x} value={String(x)}>{x === 3 ? '3 or more' : x}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Down Payment Source</Label>
                  <Select value={p.down_payment_source} onValueChange={v => set('property_loan', 'down_payment_source', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash Savings</SelectItem>
                      <SelectItem value="epf">EPF Account 2</SelectItem>
                      <SelectItem value="family_loan">Family Loan ⚠</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 grid grid-cols-3 gap-2">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={p.new_development} onCheckedChange={v => set('property_loan', 'new_development', !!v)} /> New development</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={p.subsales} onCheckedChange={v => set('property_loan', 'subsales', !!v)} /> Subsales</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={p.bumi_unit} onCheckedChange={v => set('property_loan', 'bumi_unit', !!v)} /> Bumi unit</label>
                </div>
              </div>
            )}

            {/* STEP 4 — CTOS */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-indigo-300 rounded-lg p-4 bg-indigo-50/40">
                  <Label className="flex items-center gap-2"><Upload className="w-4 h-4" /> Upload CTOS Report PDF * (mandatory)</Label>
                  <Input type="file" accept="application/pdf" onChange={e => onPdfChange(e.target.files[0] || null)} className="mt-2" data-testid="aw-ctos-pdf" />
                  {pdfFile && (
                    <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                      <FileText className="w-3 h-3" /> {pdfFile.name} ({Math.round(pdfFile.size / 1024)} KB)
                    </p>
                  )}
                  {extracting && (
                    <p className="text-xs text-indigo-700 mt-2 flex items-center gap-1" data-testid="aw-ctos-extracting">
                      <Loader2 className="w-3 h-3 animate-spin" /> AI is reading your CTOS report… (~5-15 sec)
                    </p>
                  )}
                  {extractedAt && (
                    <p className="text-xs text-emerald-700 mt-2 flex items-center gap-1" data-testid="aw-ctos-extracted">
                      🤖 Auto-extracted at {extractedAt.toLocaleTimeString()}. {extractNote || ''} Please verify the 6 fields below before submitting.
                    </p>
                  )}
                  {!extracting && !extractedAt && extractNote && (
                    <p className="text-xs text-amber-700 mt-2">{extractNote}</p>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {extractedAt
                    ? '✏️ Review and edit the AI-detected values if needed. These drive the assessment math.'
                    : 'Key in the 6 critical fields from the report you just uploaded — these drive the assessment math.'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>CTOS Score (300-850) *</Label><Input type="number" min={300} max={850} value={c.ctos_score} onChange={e => set('ctos', 'ctos_score', parseInt(e.target.value || 0))} data-testid="aw-ctos-score" /></div>
                  <div><Label>Total CCRIS monthly commitments (RM)</Label><Input type="number" value={c.total_ccris_monthly_commitments} onChange={e => set('ctos', 'total_ccris_monthly_commitments', parseFloat(e.target.value || 0))} data-testid="aw-ccris-commitments" /></div>
                  <div><Label>Active facilities count</Label><Input type="number" min={0} value={c.active_facilities_count} onChange={e => set('ctos', 'active_facilities_count', parseInt(e.target.value || 0))} /></div>
                  <div><Label>Late payments (12mo)</Label><Input type="number" min={0} value={c.late_payments_12mo_count} onChange={e => set('ctos', 'late_payments_12mo_count', parseInt(e.target.value || 0))} /></div>
                  <div><Label>Special-attention flags</Label><Input type="number" min={0} value={c.special_attention_flags_count} onChange={e => set('ctos', 'special_attention_flags_count', parseInt(e.target.value || 0))} /></div>
                </div>
                <div className="grid grid-cols-3 gap-2 border-t pt-3">
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={c.has_court_action} onCheckedChange={v => set('ctos', 'has_court_action', !!v)} data-testid="aw-court" /> Court action</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={c.has_bankruptcy} onCheckedChange={v => set('ctos', 'has_bankruptcy', !!v)} data-testid="aw-bankruptcy" /> Bankruptcy</label>
                  <label className="flex items-center gap-2 text-sm"><Checkbox checked={c.has_dcheqs} onCheckedChange={v => set('ctos', 'has_dcheqs', !!v)} /> DCHEQS</label>
                </div>
                <div><Label>Notes (optional)</Label><Textarea rows={2} value={c.raw_notes} onChange={e => set('ctos', 'raw_notes', e.target.value)} /></div>
              </div>
            )}

            {/* STEP 5 — Consent */}
            {step === 4 && (
              <div className="space-y-3">
                <div className="border rounded-lg p-4 bg-slate-50 text-sm text-slate-700 max-h-60 overflow-y-auto">
                  <p className="font-semibold mb-2">Personal Data Protection Act 2010 (PDPA) — Notice & Consent</p>
                  <p>By proceeding, you confirm that the applicant has authorized SAM Rumah to:</p>
                  <ul className="list-disc ml-5 mt-2 space-y-1">
                    <li>Collect, store, and process the personal and financial information submitted in this form.</li>
                    <li>Hold the uploaded CTOS / CCRIS report as evidence for the duration of this loan application.</li>
                    <li>Share the assessment outcome (risk band + recommended banks) with participating banks for loan pre-qualification.</li>
                    <li>Retain this data for audit purposes per Bank Negara Malaysia requirements (typically 7 years).</li>
                  </ul>
                  <p className="mt-2">Applicant retains the right to access, correct, or request deletion of this data subject to regulatory holds.</p>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox checked={form.pdpa_consent} onCheckedChange={v => setForm(prev => ({ ...prev, pdpa_consent: !!v }))} data-testid="aw-pdpa-consent" />
                  <span className="text-sm">I confirm that the applicant has provided informed consent for this assessment in accordance with the PDPA 2010, and I am authorized to upload this CTOS report on their behalf.</span>
                </label>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={back} disabled={step === 0 || busy} data-testid="aw-back">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button onClick={next} disabled={busy} data-testid="aw-next">
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={busy || !form.pdpa_consent} className="bg-indigo-600 hover:bg-indigo-700" data-testid="aw-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" /> Run Assessment</>}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
